import { z } from 'zod';
import {
  ACCESS_CONTROL_PROVIDERS,
  EMAIL_PROVIDERS,
  INTEGRATION_KINDS,
  INTEGRATION_STATUSES,
  PAYMENT_PROVIDERS,
  SMS_PROVIDERS,
  STORAGE_PROVIDERS,
} from '@bulk/types';
import { idSchema } from './common';

export const tbcConfigSchema = z.object({
  apiKey: z.string().min(1),
  clientId: z.string().min(1),
  clientSecret: z.string().min(1),
  baseUrl: z.url().default('https://api.tbcbank.ge'),
  callbackAllowedIps: z
    .array(z.string())
    .default(['193.104.20.44', '193.104.20.45', '185.52.80.44', '185.52.80.45']),
});
export type TbcConfig = z.infer<typeof tbcConfigSchema>;

export const bogConfigSchema = z.object({
  clientId: z.string().min(1),
  clientSecret: z.string().min(1),
  baseUrl: z.url().default('https://api.bog.ge'),
  tokenUrl: z.url().default('https://oauth2.bog.ge/auth/realms/bog/protocol/openid-connect/token'),
  verifyCallbackSignature: z.boolean().default(true),
});
export type BogConfig = z.infer<typeof bogConfigSchema>;

export const mockPaymentConfigSchema = z.object({
  autoComplete: z.boolean().default(false),
  declineSavedCards: z.boolean().default(false),
});

export const envisoConfigSchema = z.object({
  apiKey: z.string().min(1),
  apiSecret: z.string().min(1),
  tenantKey: z.string().min(1),
  environment: z.enum(['production', 'staging']).default('staging'),
});
export type EnvisoConfig = z.infer<typeof envisoConfigSchema>;

export const gantnerAgentConfigSchema = z.object({
  agentToken: z.string().min(16),
  controllerNotes: z.string().max(2000).nullable().default(null),
});
export type GantnerAgentConfig = z.infer<typeof gantnerAgentConfigSchema>;

export const mockAccessConfigSchema = z.object({
  simulateProviderErrors: z.boolean().default(false),
});

export const smtpConfigSchema = z.object({
  host: z.string().min(1),
  port: z.number().int().min(1).max(65535).default(587),
  secure: z.boolean().default(false),
  username: z.string().default(''),
  password: z.string().default(''),
  fromEmail: z.email(),
  fromName: z.string().max(120).default(''),
});
export type SmtpConfig = z.infer<typeof smtpConfigSchema>;

export const resendConfigSchema = z.object({
  apiKey: z.string().min(1),
  fromEmail: z.email(),
  fromName: z.string().max(120).default(''),
});
export type ResendConfig = z.infer<typeof resendConfigSchema>;

export const consoleEmailConfigSchema = z.object({
  fromEmail: z.email().default('no-reply@example.test'),
  fromName: z.string().max(120).default('Bulk'),
});

export const storageConfigSchema = z.object({});

const smsSenderSchema = z
  .string()
  .trim()
  .min(1)
  .max(11)
  .regex(/^[A-Za-z0-9.-]+$/, 'Latin letters, digits, dots and dashes only');

const marketingOptOutSchema = z.string().trim().max(40).default('').describe("smsoffice.ge's opt-out line for the sender, e.g. 90212 1234 - NO; campaign texts are refused without it");

export const smsofficeConfigSchema = z.object({
  apiKey: z.string().min(1),
  sender: smsSenderSchema,
  marketingOptOut: marketingOptOutSchema,
});
export type SmsofficeConfig = z.infer<typeof smsofficeConfigSchema>;

export const consoleSmsConfigSchema = z.object({
  sender: smsSenderSchema.default('Bulk'),
  marketingOptOut: marketingOptOutSchema,
});

export const providerConfigSchemas = {
  PAYMENT: { mock: mockPaymentConfigSchema, tbc: tbcConfigSchema, bog: bogConfigSchema },
  ACCESS_CONTROL: {
    mock: mockAccessConfigSchema,
    enviso: envisoConfigSchema,
    gantner_agent: gantnerAgentConfigSchema,
  },
  EMAIL: { console: consoleEmailConfigSchema, smtp: smtpConfigSchema, resend: resendConfigSchema },
  STORAGE: { local: storageConfigSchema, supabase: storageConfigSchema },
  SMS: { console: consoleSmsConfigSchema, smsoffice: smsofficeConfigSchema },
} as const;

export const SECRET_CONFIG_FIELDS: Record<string, readonly string[]> = {
  tbc: ['apiKey', 'clientSecret'],
  bog: ['clientSecret'],
  enviso: ['apiSecret'],
  gantner_agent: ['agentToken'],
  smtp: ['password'],
  resend: ['apiKey'],
  smsoffice: ['apiKey'],
};

export const providerKeySchema = z.union([
  z.enum(PAYMENT_PROVIDERS),
  z.enum(ACCESS_CONTROL_PROVIDERS),
  z.enum(EMAIL_PROVIDERS),
  z.enum(STORAGE_PROVIDERS),
  z.enum(SMS_PROVIDERS),
]);

export const configureIntegrationSchema = z.object({
  kind: z.enum(INTEGRATION_KINDS),
  provider: providerKeySchema,
  config: z.record(z.string(), z.unknown()).default({}),
});
export type ConfigureIntegrationInput = z.infer<typeof configureIntegrationSchema>;

export const integrationSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  kind: z.enum(INTEGRATION_KINDS),
  provider: z.string(),
  status: z.enum(INTEGRATION_STATUSES),
  enabled: z.boolean(),
  config: z.record(z.string(), z.unknown()),
  connectedAt: z.string().nullable(),
  lastCheckedAt: z.string().nullable(),
  lastError: z.string().nullable(),
  webhookUrl: z.string().nullable(),
  platformEmailSender: z.object({ fromEmail: z.string(), brand: z.string() }).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type IntegrationDto = z.infer<typeof integrationSchema>;

export const rotatedAgentTokenSchema = z.object({
  integration: integrationSchema,
  agentToken: z.string().describe('The new token, in this answer only; the one before it stops working at once'),
});
export type RotatedAgentTokenDto = z.infer<typeof rotatedAgentTokenSchema>;

export const connectionTestResultSchema = z.object({
  ok: z.boolean(),
  status: z.enum(INTEGRATION_STATUSES),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
  checkedAt: z.string(),
});
export type ConnectionTestResultDto = z.infer<typeof connectionTestResultSchema>;

export const sendTestSmsSchema = z.object({
  to: z.string().trim().min(1).max(32).describe('A mobile number: a Georgian one written locally, any other with its + country code'),
});
export type SendTestSmsInput = z.infer<typeof sendTestSmsSchema>;

export const integrationLogSchema = z.object({
  id: idSchema,
  integrationId: idSchema,
  level: z.enum(['INFO', 'WARN', 'ERROR']),
  operation: z.string(),
  message: z.string(),
  durationMs: z.number().int().nullable(),
  createdAt: z.string(),
});
export type IntegrationLogDto = z.infer<typeof integrationLogSchema>;
